import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './styles.module.css';

interface ISsoContinueStepProps {
  email: string;
  /** The company's name from discovery; the copy reads generically without it. */
  displayName?: string;
  /** The company's logo from discovery; its name (or the address's domain) stands in without it. */
  logoUrl?: string;
  /** Shown above the button — e.g. why the password step sent the user here. */
  notice: string | null;
  onContinue: () => void;
  onUseDifferentAccount: () => void;
}

/**
 * The email-first step's SSO branch: this address signs in with its company
 * account, so there is no password field at all.
 *
 * The step leads with a hand-off — d-orca → the company — because leaving for
 * another site is the moment SSO most often confuses people; showing it makes
 * the redirect expected. One conventional button, and a quiet "not you?".
 */
export function SsoContinueStep(props: ISsoContinueStepProps) {
  const { email, displayName, logoUrl, notice, onContinue, onUseDifferentAccount } = props;
  const { t } = useTranslation();
  // The navigation takes a moment to start; a second click must not race it.
  const [leaving, setLeaving] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo: boolean = logoUrl !== undefined && !logoFailed;
  // Without a logo the company tile carries a name: the company's, or the address's domain.
  const companyLabel: string = displayName ?? email.slice(email.lastIndexOf('@') + 1);

  return (
    <div className={styles.ssoStep}>
      <div className={styles.handoff}>
        <span className={styles.handoffTile}>
          <img src="/orca.png" alt="d-orca" />
        </span>
        <span className={styles.handoffArrow} aria-hidden>
          <i />
          <i />
          <i />
          <svg viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {showLogo ? (
          <span className={styles.handoffTile}>
            <img src={logoUrl} alt={companyLabel} onError={() => setLogoFailed(true)} />
          </span>
        ) : (
          <span className={`${styles.handoffTile} ${styles.handoffTileName}`}>{companyLabel}</span>
        )}
      </div>

      <h1 className={styles.ssoTitle}>
        {displayName
          ? t('auth.login.ssoTitleWith', { name: displayName })
          : t('auth.login.ssoTitleGeneric')}
      </h1>
      <p className={styles.ssoWho}>
        <Trans i18nKey="auth.login.ssoSigningInAs" values={{ email }} components={{ b: <strong /> }} />
      </p>

      {notice ? (
        <div className={styles.notice} role="status">
          {notice}
        </div>
      ) : null}

      <button
        type="button"
        className={styles.submit}
        disabled={leaving}
        autoFocus
        onClick={() => {
          setLeaving(true);
          onContinue();
        }}
      >
        {leaving ? <span className={styles.spinner} aria-hidden /> : null}
        <span>{leaving ? t('auth.login.ssoRedirecting') : t('auth.login.continue')}</span>
        {leaving ? null : (
          <svg className={styles.arrow} viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <p className={styles.ssoNotYou}>
        {t('auth.login.ssoNotYou')}{' '}
        <button type="button" className={styles.linkButton} disabled={leaving} onClick={onUseDifferentAccount}>
          {t('auth.login.ssoUseAnotherEmail')}
        </button>
      </p>

      <footer className={styles.footer}>
        <span>{t('auth.login.footerHelp')}</span>
        <span className={styles.footerSep} aria-hidden>
          ·
        </span>
        <span>{t('auth.login.footerContact')}</span>
      </footer>
    </div>
  );
}
